import { useGoogleCalendarsList } from "@/components/EntitySelector/integrations/GoogleCalendarSelector";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { GoogleCalendarCalendarEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Calendar, ChevronsUpDown, X } from "lucide-react";
import React, { useMemo, useState } from "react";

interface GoogleCalendarControlProps extends ControlProps {
  data: GoogleCalendarCalendarEntity | string | undefined;
}

const GoogleCalendarControlComponent: React.FC<GoogleCalendarControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("google_calendar");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const { data: calendarsResponse, isLoading } = useGoogleCalendarsList(
    integration!,
  );

  const calendars: GoogleCalendarCalendarEntity[] =
    calendarsResponse?.data || [];

  const filterFn = (entity: GoogleCalendarCalendarEntity) => {
    const calendar = entity.data;
    const { title = "", summary = "", description = "" } = calendar;
    const query = searchQuery.toLowerCase();
    return (
      title.toLowerCase().includes(query) ||
      summary.toLowerCase().includes(query) ||
      description?.toLowerCase().includes(query) ||
      calendar.id.toLowerCase().includes(query)
    );
  };

  const renderCalendarIcon = () => {
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <Calendar className="w-4! h-4!" />
      </div>
    );
  };

  const selectedCalendar = useMemo(
    () => calendars.find((calendar) => selectedId === calendar.data.id),
    [calendars, selectedId],
  );

  if (!visible) return null;

  return (
    <div className="w-full">
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={!enabled}
          >
            {selectedCalendar ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderCalendarIcon()}
                <span className="truncate">
                  {selectedCalendar.data.summary || selectedCalendar.data.title}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select calendar...</span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedCalendar && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search calendars..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No calendars found.</CommandEmpty>

                  {/* Primary Calendar */}
                  {calendars.filter((c) => c.data.primary && filterFn(c))
                    .length > 0 && (
                    <CommandGroup heading="Primary Calendar">
                      {calendars
                        .filter((c) => c.data.primary && filterFn(c))
                        .map((entity) => (
                          <CommandItem
                            key={entity.data.id}
                            value={entity.data.id}
                            onSelect={() => {
                              // Return full entity object
                              handleChange(path, entity);
                              setOpen(false);
                            }}
                          >
                            {renderCalendarIcon()}
                            <span className="truncate">
                              {entity.data.summary || entity.data.title}
                            </span>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}

                  {/* Other Calendars */}
                  {calendars.filter((c) => !c.data.primary && filterFn(c))
                    .length > 0 && (
                    <CommandGroup heading="Other Calendars">
                      {calendars
                        .filter((c) => !c.data.primary && filterFn(c))
                        .sort((a, b) =>
                          (a.data.summary || a.data.title).localeCompare(
                            b.data.summary || b.data.title,
                          ),
                        )
                        .map((entity) => (
                          <CommandItem
                            key={entity.data.id}
                            value={entity.data.id}
                            onSelect={() => {
                              // Return full entity object
                              handleChange(path, entity);
                              setOpen(false);
                            }}
                          >
                            {renderCalendarIcon()}
                            <span className="truncate">
                              {entity.data.summary || entity.data.title}
                            </span>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const GoogleCalendarControlTester = rankWith(
  10,
  optionIs("control", "google_calendar_calendar"),
);

export const GoogleCalendarControl = withJsonFormsControlProps(
  GoogleCalendarControlComponent,
);
